/* eslint-disable max-len */
import React, { useState, useEffect } from 'react'
import {
  ORDER_DISCOUNT_CONDITION_MIN_SPEND,
  ORDER_DISCOUNT_CONDITION_MIN_QUANTITY,
  ORDER_DISCOUNT_TYPE_FIXED,
  ORDER_DISCOUNT_TYPE_PERCENTAGE,
  BUYER_ORDER_PAID,
  BUYER_ORDER_COLLECTED,
} from '_utils/constant'
import orderApi from '_api/order'
import EventNote from './EventNote'
import EventExternalURL from './EventExternalURL'
// import { AddDeliveryOption } from './AddDeliveryOption'

const Note = ({ register, hiddenNote: defaultVal, setValue, watch, control, getValues }) => {
  const [checked, setChecked] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  const toggleModal = () => {
    setOpenModal(!openModal)
  }

  const handleChange = (e) => {
    const isCheck = e.target.checked
    setChecked(isCheck)
    if (isCheck) {
      setOpenModal(true)
    } else {
      setValue('termConds', [])
    }
  }

  useEffect(() => {
    setChecked(defaultVal)
  }, [defaultVal])

  return (
    <React.Fragment>
      <div className='havebg'>
        <div className='row'>
          <div className='col-12'>
            <div className='custom-control custom-checkbox'>
              <input
                id='customCheck3'
                type='checkbox'
                onChange={(e) => handleChange(e)}
                className='custom-control-input'
                checked={checked}
              />
              <label className='custom-control-label' htmlFor='customCheck3'>
                Add important notes to buyers
              </label>
            </div>
          </div>
        </div>
      </div>
      {openModal && (
        <EventNote
          modal={openModal}
          toggle={toggleModal}
          register={register}
          watch={watch}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
    </React.Fragment>
  )
}

const ExternalURLs = ({ externalUrl, hiddenExternalURL: defaultVal, setExternalUrl }) => {
  const [checked, setChecked] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  const toggleModal = () => {
    setOpenModal(!openModal)
  }

  const handleChange = (e) => {
    const isCheck = e.target.checked
    setChecked(isCheck)
    if (isCheck) {
      setOpenModal(true)
    } else {
      setExternalUrl(null)
    }
  }

  useEffect(() => {
    setChecked(defaultVal)
  }, [defaultVal])

  return (
    <React.Fragment>
      <div className='havebg'>
        <div className='row'>
          <div className='col-12'>
            <div className='custom-control custom-checkbox'>
              <input
                id='extraFieldCheckBox'
                type='checkbox'
                onChange={(e) => handleChange(e)}
                className='custom-control-input'
                checked={checked}
              />
              <label className='custom-control-label' htmlFor='extraFieldCheckBox'>
                Add external URL
              </label>
            </div>
            <small className='error'></small>
          </div>
        </div>
      </div>
      <EventExternalURL
        modal={openModal}
        toggle={toggleModal}
        value={externalUrl}
        onChange={setExternalUrl}
      />
    </React.Fragment>
  )
}

const DiscountOptions = ({ setValue, eventData, hasPaidOrder }) => {
  const [checked, setChecked] = useState(false)
  const [discount, setDiscount] = useState(null)
  const [isOpenForm, setIsOpenForm] = useState(false)

  const [condType, setCondType] = useState(ORDER_DISCOUNT_CONDITION_MIN_SPEND)
  const [condVal, setCondVal] = useState('')
  const [discType, setDiscType] = useState(ORDER_DISCOUNT_TYPE_PERCENTAGE)
  const [discAmt, setDiscAmt] = useState('')

  const resetForm = () => {
    setCondType(ORDER_DISCOUNT_CONDITION_MIN_SPEND)
    setCondVal('')
    setDiscType(ORDER_DISCOUNT_TYPE_PERCENTAGE)
    setDiscAmt('')
  }

  useEffect(() => {
    if (eventData) {
      const enabled = !!eventData?.orderDiscountEnabled
      setChecked(enabled)
      setValue('orderDiscountEnabled', enabled)

      if (eventData?.orderDiscount) {
        setDiscount(eventData.orderDiscount)
        setValue('orderDiscount', eventData.orderDiscount)

        setCondType(eventData.orderDiscount.condition)
        setCondVal(eventData.orderDiscount.threshold)
        setDiscType(eventData.orderDiscount.type)
        setDiscAmt(eventData.orderDiscount.amount)
      }
    }
  }, [eventData, setValue])

  const handleCheckboxChange = (e) => {
    const isCheck = e.target.checked
    setChecked(isCheck)
    setValue('orderDiscountEnabled', isCheck)
    if (isCheck) {
      if (!discount) {
        setIsOpenForm(true)
      }
    } else {
      setDiscount(null)
      setValue('orderDiscount', null)
      setIsOpenForm(false)
    }
  }

  const handleSave = () => {
    if (condVal === '' || discAmt === '') return

    const discountObj = {
      condition: condType,
      threshold: Number(condVal),
      type: discType,
      amount: Number(discAmt),
    }

    setDiscount(discountObj)
    setValue('orderDiscount', discountObj)
    setValue('orderDiscountEnabled', true)
    resetForm()
    setIsOpenForm(false)
  }

  const handleCancel = () => {
    resetForm()
    setIsOpenForm(false)

    if (!discount) {
      setChecked(false)
      setValue('orderDiscountEnabled', false)
    }
  }

  const handleDelete = () => {
    setDiscount(null)
    setValue('orderDiscount', null)
    resetForm()
    setIsOpenForm(true)
  }

  const handleEdit = () => {
    if (discount) {
      setCondType(discount.condition)
      setCondVal(discount.threshold)
      setDiscType(discount.type)
      setDiscAmt(discount.amount)
      setIsOpenForm(true)
    }
  }

  const getDiscountDisplayText = () => {
    if (!discount) return ''
    const hasThreshold = Number(discount?.threshold) > 0
    const conditionText = hasThreshold
      ? (discount.condition === ORDER_DISCOUNT_CONDITION_MIN_SPEND
        ? `Spend $${Number(discount.threshold).toFixed(2)}+`
        : `Order ${discount.threshold} + items`)
      : ''
    const discountText = discount.type === ORDER_DISCOUNT_TYPE_FIXED
      ? `$${Number(discount.amount).toFixed(2)} off`
      : `${discount.amount}% off`
    return conditionText ? `${conditionText} → ${discountText}` : discountText
  }

  const isFormValid = condVal !== '' && Number(condVal) >= 0 && discAmt !== '' && Number(discAmt) >= 0

  return (
    <React.Fragment>
      <div className='havebg'>
        <div className='custom-control custom-checkbox'>
          <input
            name='addDiscountOption'
            type='checkbox'
            className='custom-control-input'
            id='addDiscountOptionCheckbox'
            checked={checked}
            onChange={handleCheckboxChange}
            disabled={hasPaidOrder}
          />
          <label className='custom-control-label' htmlFor='addDiscountOptionCheckbox'>
            Add Discount Options
          </label>
        </div>
      </div>

      {checked && (
        <div className='discount-config-container' style={{ marginBottom: '10px' }}>
          {discount && (
            <div className='tier-list-container'>
              <div className='tier-item'>
                <div className='tier-info'>
                  <span className='tier-tag'>🏷</span>
                  <div className='tier-info-content'>
                    <span className='tier-text'>
                      {getDiscountDisplayText()}
                    </span>
                  </div>
                </div>
                <div className='tier-actions'>
                  {!hasPaidOrder && (
                    <>
                      <button
                        type='button'
                        className='tier-action-btn btn-edit'
                        onClick={handleEdit}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        type='button'
                        className='tier-action-btn btn-delete'
                        onClick={handleDelete}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {isOpenForm && (
            <div className='tier-form'>
              <div className='tier-form-title'>
                Discount Settings
              </div>
              <div className='tf-row'>
                <span className='tf-lbl'>Condition</span>
                <select
                  className='tier-select'
                  value={condType}
                  onChange={(e) => setCondType(e.target.value)}
                  style={{ gridColumn: 'span 2' }}
                >
                  <option value={ORDER_DISCOUNT_CONDITION_MIN_SPEND}>Min. spend</option>
                  <option value={ORDER_DISCOUNT_CONDITION_MIN_QUANTITY}>Min. quantity</option>
                </select>
              </div>
              <div className='tf-row'>
                <span className='tf-lbl' style={{ color: '#B4B2A9' }}>over</span>
                <div className='tf-amt-wrap' style={{ gridColumn: 'span 2' }}>
                  {condType === ORDER_DISCOUNT_CONDITION_MIN_SPEND && <span className='tf-pfx'>$</span>}
                  <input
                    className='tier-amt-input'
                    type='number'
                    min='0'
                    step={condType === ORDER_DISCOUNT_CONDITION_MIN_SPEND ? '0.50' : '1'}
                    placeholder='0'
                    value={condVal}
                    onChange={(e) => setCondVal(e.target.value)}
                  />
                  {condType === ORDER_DISCOUNT_CONDITION_MIN_QUANTITY && <span className='tf-pfx' style={{ marginLeft: '2px' }}>items</span>}
                </div>
              </div>
              <div style={{ height: '0.5px', background: '#e0dfd8' }}></div>
              <div className='tf-row'>
                <span className='tf-lbl'>Discount</span>
                <div className='type-toggle'>
                  <button
                    type='button'
                    className={`type-pill ${discType === ORDER_DISCOUNT_TYPE_FIXED ? 'active' : ''}`}
                    onClick={() => setDiscType(ORDER_DISCOUNT_TYPE_FIXED)}
                  >
                    $ Fixed
                  </button>
                  <button
                    type='button'
                    className={`type-pill ${discType === ORDER_DISCOUNT_TYPE_PERCENTAGE ? 'active' : ''}`}
                    onClick={() => setDiscType(ORDER_DISCOUNT_TYPE_PERCENTAGE)}
                  >
                    %
                  </button>
                </div>
                <div className='tf-amt-wrap'>
                  {discType === ORDER_DISCOUNT_TYPE_FIXED && <span className='tf-pfx'>$</span>}
                  <input
                    className='tier-amt-input'
                    type='number'
                    min='0'
                    step='0.5'
                    placeholder='0'
                    value={discAmt}
                    onChange={(e) => setDiscAmt(e.target.value)}
                  />
                  {discType === ORDER_DISCOUNT_TYPE_PERCENTAGE && <span className='tf-pfx' style={{ marginLeft: '2px' }}>%</span>}
                </div>
              </div>
              <div className='tier-form-footer'>
                <button type='button' className='tier-cancel-btn' onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  type='button'
                  className='tier-save-btn'
                  onClick={handleSave}
                  disabled={!isFormValid}
                >
                  Save →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  )
}

const PaymentOption = (props) => {
  const { register, paynow, setPaynow, payTogether, setPayTogether, eid, setValue, eventData } = props
  const [hasPaidOrder, setHasPaidOrder] = useState(false)
  const [checked, setChecked] = useState(false)
  const [checkedPayTogether, setCheckedPayTogether] = useState(true)
  const [requestDeliveryAddress, setRequestDeliveryAddress] = useState(false)

  useEffect(() => {
    if (eid) {
      orderApi
        .getListOrdersByEventId(eid)
        .then(({ msgResp }) => {
          if (Array.isArray(msgResp)) {
            const hasPaid = msgResp.some(
              (order) =>
                order.buyerStatus === BUYER_ORDER_PAID ||
                order.buyerStatus === BUYER_ORDER_COLLECTED
            )
            setHasPaidOrder(hasPaid)
          }
        })
        .catch((err) => {
          console.error('Error fetching event orders:', err)
        })
    } else {
      setHasPaidOrder(false)
    }
  }, [eid])

  useEffect(() => {
    setChecked(paynow)
    setCheckedPayTogether(payTogether)
  }, [paynow, payTogether])

  useEffect(() => {
    if (eid && eventData?.bookingAddressRequired !== undefined) {
      setRequestDeliveryAddress(eventData.bookingAddressRequired)
      setValue('bookingAddressRequired', eventData.bookingAddressRequired)
    }
  }, [eid, eventData?.bookingAddressRequired, setValue])

  const handleRequestDeliveryAddressChange = () => {
    const newValue = !requestDeliveryAddress
    setRequestDeliveryAddress(newValue)
    setValue('bookingAddressRequired', newValue)
  }

  return (
    <div className='payment'>
      <h4 className='c-txt-12 mb-2'>
        <strong>Payment Options</strong>
      </h4>
      <div className='c-form'>
        <div className='havebg'>
          <div className='custom-control custom-checkbox'>
            <input
              name='paynow'
              type='checkbox'
              className='custom-control-input'
              id='customCheck1'
              value={paynow}
              onChange={() => setPaynow(!paynow)}
              checked={checked}
            />
            <label className='custom-control-label' htmlFor='customCheck1'>
              Payment to be made after closing date
            </label>
            <p className='text-small'>
              This option calls for buyers to pay later. This option allow host to distribute the
              cost of delivery cost and consolidate discount at the end of the event. Delivery
              charges and discount amount are to be entered at &gt; My Event &gt; Host Dashboard
            </p>
          </div>
        </div>
        <div className='havebg'>
          <div className='custom-control custom-checkbox'>
            <input
              name='payTogether'
              type='checkbox'
              className='custom-control-input'
              id='payTogetherCheckbox'
              value={payTogether}
              onChange={() => setPayTogether(!payTogether)}
              checked={checkedPayTogether}
            />
            <label className='custom-control-label' htmlFor='payTogetherCheckbox'>
              Pay together
            </label>
            <p className='text-small'>
              This option allows buyers to pay for multiple events enabled with Pay together
            </p>
          </div>
        </div>
        <div className='havebg'>
          <div className='row'>
            <div className='col-8'>
              <div className='custom-control custom-checkbox'>
                <label className='custom-control-label'>
                  <small>Admin fees to be added to each order</small>
                </label>
              </div>
            </div>
            <div className='col-4 text-right'>
              <span className='txt-s'>$</span>
              <input
                name='adminCost'
                type='number'
                step='0.01'
                className='form-control'
                {...register('adminCost')}
                defaultValue='0.0'
                onWheel={(event) => { event.target.blur() }}
              />
            </div>
          </div>
        </div>
        <Note {...props} />
        <ExternalURLs {...props} />

        {(!eid ||
          eventData?.orderDiscountEnabled === true ||
          hasPaidOrder === false) && (
          <DiscountOptions {...props} hasPaidOrder={hasPaidOrder} />
        )}

        <div className='havebg'>
          <div className='custom-control custom-checkbox'>
            <input
              name='requestDeliveryAddress'
              type='checkbox'
              className='custom-control-input'
              id='requestDeliveryAddressCheckbox'
              value={requestDeliveryAddress}
              onChange={handleRequestDeliveryAddressChange}
              checked={requestDeliveryAddress}
            />
            <label className='custom-control-label' htmlFor='requestDeliveryAddressCheckbox'>
              Request for Delivery Details
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentOption
